// hooks/user/useAuditLog.js
import { useQuery } from "react-query";
import axios from "axios";
import { useAuth0 } from "@auth0/auth0-react";

/**
 * Custom hook for fetching audit logs (admin only)
 *
 * Provides access to verification change logs and other admin actions.
 * Requirements: 11.4, 11.5
 *
 * @param {Object} options - Query options
 * @param {string} options.userId - Filter by specific user ID (optional)
 * @param {string} options.action - Filter by action type (optional)
 * @param {number} options.page - Page number (1-based)
 * @param {number} options.limit - Items per page
 * @returns {Object} Query result with audit logs data
 */
export const useAuditLog = ({
  userId = null,
  action = null,
  page = 1,
  limit = 50,
} = {}) => {
  const { getAccessTokenSilently } = useAuth0();
  const apiUrl = process.env.REACT_APP_API_URL;

  return useQuery(
    ["auditLog", { userId, action, page, limit }],
    async () => {
      const token = await getAccessTokenSilently();

      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      if (userId) {
        params.append("userId", userId);
      }

      if (action) {
        params.append("action", action);
      }

      const response = await axios.get(
        `${apiUrl}/api/admin/audit-log?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          timeout: 15000,
        }
      );

      return response.data;
    },
    {
      enabled: !!getAccessTokenSilently,
      staleTime: 1 * 60 * 1000, // 1 minute - audit logs should be fresh
      cacheTime: 5 * 60 * 1000, // 5 minutes
      retry: (failureCount, error) => {
        // Don't retry authorization errors
        if (error.response?.status === 401 || error.response?.status === 403) {
          return false;
        }
        return failureCount < 2;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 3000),
    }
  );
};
